// VideoReplyComposer.tsx — inline reply input for the video comment thread.
// Stateful for its own draft; reports changes upward for the discard guard.
import { useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@/ui';

export interface VideoReplyComposerProps {
  replyingToHandle: string | null;
  onSubmit: (body: string) => void;
  onCancel: () => void;
  onDraftChange: (draft: string) => void;
  /**
   * True when this composer is rendered inside VideoCommentsSection's
   * RepliesSection (already indented 54px) — replying to a NESTED comment.
   * Without this, the composer's own 54px margin stacked on top of the
   * RepliesSection's 54px margin, double-indenting to 108px. Default false
   * (top-level reply: not yet inside an indented container, so it carries
   * its own 54px to align under the avatar).
   */
  nested?: boolean;
}

const Wrap = styled.div<{ $nested: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 8px ${({ $nested }) => ($nested ? '0' : '54px')};
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Mention = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
`;

const Input = styled.textarea`
  width: 100%;
  min-height: 48px;
  resize: none;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:focus-visible {
    border-bottom-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export function VideoReplyComposer({
  replyingToHandle,
  onSubmit,
  onCancel,
  onDraftChange,
  nested = false,
}: VideoReplyComposerProps) {
  const [value, setValue] = useState('');
  const trimmed = value.trim();

  function change(e: ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value);
    onDraftChange(e.target.value);
  }

  function submit() {
    if (!trimmed) return;
    onSubmit(trimmed);
  }

  function keydown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <Wrap $nested={nested}>
      <Head>
        <span>
          {replyingToHandle ? (
            <>
              Replying to <Mention>{replyingToHandle}</Mention>
            </>
          ) : (
            'Add a reply'
          )}
        </span>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </Head>
      <Input
        value={value}
        onChange={change}
        onKeyDown={keydown}
        placeholder="Add a reply…"
        aria-label="Reply"
        autoFocus
      />
      <Actions>
        <Button variant="primary" size="sm" disabled={!trimmed} onClick={submit}>
          Post
        </Button>
      </Actions>
    </Wrap>
  );
}
